"use client";

import { type ReactNode, useEffect } from "react";

import { CenteredLoading } from "@/components/common/CenteredLoading";
import { ALL_FACILITIES_ID, useFacility } from "@/contexts/FacilityContext";
import { appendFacilityScopeToUrl } from "@/lib/facilityModeAccess";
import { useRouter, useSearchParams } from "@/lib/router-compat";

/**
 * `facilityId` をURL契約に持つコース詳細群と、サイドバーの表示施設を整合させる。
 *
 * 組織全体から詳細を開く場合は対象コースの施設をそのまま閲覧できる。
 * 個別施設へ切り替えた場合は同じIDを別施設の更新先として流用せず、
 * 選択施設で絞った一覧へ安全に戻す。
 */
export function CourseFacilityScopeGuard({
  children,
}: {
  children: ReactNode;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { facilities, isLoading, selectedFacilityId } = useFacility();
  const courseFacilityId = searchParams.get("facilityId") ?? "";
  const courseFacilityIsAvailable = facilities.some(
    (facility) => facility.id === courseFacilityId
  );
  const isSelectedFacility = selectedFacilityId !== ALL_FACILITIES_ID;
  const hasScopeMismatch =
    !courseFacilityIsAvailable ||
    (isSelectedFacility && courseFacilityId !== selectedFacilityId);

  useEffect(() => {
    if (isLoading || !hasScopeMismatch) return;

    const facilityFilter = isSelectedFacility
      ? `?facilityId=${encodeURIComponent(selectedFacilityId)}`
      : "";
    router.replace(
      appendFacilityScopeToUrl(
        `/courses${facilityFilter}`,
        selectedFacilityId || ALL_FACILITIES_ID
      )
    );
  }, [
    hasScopeMismatch,
    isLoading,
    isSelectedFacility,
    router,
    selectedFacilityId,
  ]);

  if (isLoading || hasScopeMismatch) {
    return <CenteredLoading message="表示する施設を確認しています…" />;
  }

  return children;
}
